// Meeting log — the changelog's primary source.
//
// Each `Meeting` is one synchronous discussion. Each `MeetingEntry` is one
// salient discussion point or decision from that meeting, citing who said it
// (verbatim) and linking to the resulting action(s). The changelog scene
// renders this as a meeting-first timeline with citation chips that open a
// drawer with the verbatim quote + action detail + shipped state.
//
// Single source of truth — the /changelog skill appends here when a meeting's
// outputs land (via /meeting), and flips `state: 'shipped'` with the commit
// ref when a delivery commit references the action ID.
//
// Curated. Not every action is logged here — only the discussion points worth
// surfacing on the meeting timeline. The full action register lives in
// docs/meetings/<date>-<slug>-actions.md.
//
// OptiFlow fork: meeting timeline is this project's meetings only.
// Foundation salvage / parity-closeout / changelog-hardening seeds were
// cleared as fork.B2 (checklist item 7). Historical wiki pages remain
// under docs/meetings/ but are not the live /changelog narrative.

export type ActionState =
  | 'shipped' // delivered in a version cut
  | 'in-flight' // spec scaffolded or implementation started; not shipped
  | 'decided' // QnA resolved but UI not yet built
  | 'planned' // routed in roadmap, not started
  | 'blocked'; // awaiting decision (qna pending)

export type ActionRef = {
  /** Full action id, e.g. 'kickoff.A1' — globally unique. */
  id: string;
  /** Short id, e.g. 'A1' — used on chips. */
  shortId: string;
  /** Action title from the actions file. */
  title: string;
  state: ActionState;
  /** Version where this landed, when shipped (e.g. 'v1.1.0'). */
  shippedIn?: string;
  /** Short SHA of the delivery commit. */
  shippedCommit?: string;
};

export type MeetingEntry = {
  /** Stable id within the meeting — usually mirrors the primary action id. */
  id: string;
  /** Short summary of the discussion point — 4-12 words. */
  text: string;
  /** Who said the load-bearing line. Order matters: primary actor first. */
  actors: string[];
  /** Verbatim quote from the transcript / cleaned input. */
  quote: string;
  /** One or more action refs that fell out of this discussion point. */
  actions: ActionRef[];
};

export type Meeting = {
  /** Slug-ish id used in URLs and cross-references. */
  id: string;
  /** ISO date the meeting happened. */
  date: string;
  /** Display title. */
  title: string;
  /** Attendees as first-name strings (or 'External' tags). */
  attendees: string[];
  /** Path to the cleaned input doc. */
  inputPath: string;
  /** Path to the structured actions file. */
  actionsPath: string;
  /** Entries shown on the timeline. Newest meaningful items first. */
  entries: MeetingEntry[];
};

// Newest meetings first — the timeline reads top-down.
export const MEETINGS: Meeting[] = [
  {
    id: "optiflow-0923",
    date: "2026-09-23",
    title: "OptiFlow update — narrow the Komatsu demo to the value story and the diagnosis",
    attendees: [
      "Miles",
      "Paul",
      "Greg",
      "Tim",
      "Nirman"
    ],
    inputPath: "docs/meetings/inputs/2026-09-23-optiflow-update.md",
    actionsPath: 'docs/meetings/2026-09-23-optiflow-update-actions.md',
    entries: [
      {
        id: "optiflow-0923.A1",
        text: "Nail the top one or two value drivers for Komatsu",
        actors: [
          "Greg"
        ],
        quote: "let's just make sure we the top one and two, we nail really well, and the rest, we just leave away.",
        actions: [
          {
            id: "optiflow-0923.A1",
            shortId: "A1",
            title: "Four-beat Komatsu demo: value at stake, the flow, the living FMEA, capacity and uptime (komatsu arc)",
            state: "in-flight"
          }
        ]
      },
      {
        id: "optiflow-0923.A2",
        text: "Diagnosis over the FMEA, TARP and Control Plan is the killer part",
        actors: [
          "Tim",
          "Miles"
        ],
        quote: "I feel like that's the killer part. Right?",
        actions: [
          {
            id: "optiflow-0923.A2",
            shortId: "A2",
            title: "Ranked FMEA causes in the engine, a recorded agent run and the case page (K4, K6, W2)",
            state: "in-flight"
          }
        ]
      },
      {
        id: "optiflow-0923.A3",
        text: "A mock that looks finished beats partial functionality",
        actors: [
          "Greg"
        ],
        quote: "even if we had a mock that looks and feels perfect, that doesn't have the functionality in, I'd I'd be happy with that as long as it's you know, we're we're you know, it's nailed and it looks professional",
        actions: [
          {
            id: "optiflow-0923.A3",
            shortId: "A3",
            title: "Opus polish arc with a comp-fidelity gate, and one outside critique (polish.P1–P5, W5)",
            state: "in-flight"
          }
        ]
      },
      {
        id: "optiflow-0923.A4",
        text: "Open on recovery and uptime in money",
        actors: [
          "Greg"
        ],
        quote: "imagine if you had an extra percentage or two of processing time processing plant recovery, … And then imagine if you had your uptime was up, you know, 5%.",
        actions: [
          {
            id: "optiflow-0923.A4",
            shortId: "A4",
            title: "Recovery · Throughput · Uptime A$/yr band on the flow landing (W1, D11, D12)", // currency-ok: 23 Sep meeting action title, mirrors the dated actions file (pre-D26)
            state: "in-flight"
          }
        ]
      },
      {
        id: "optiflow-0923.A5",
        text: "Capacity: potential against actual and the next bottleneck",
        actors: [
          "Tim",
          "Paul"
        ],
        quote: "if the grinding mill's a bottleneck and we fix that, where where's the next bottleneck after that? And what's the value of that you're obtaining",
        actions: [
          {
            id: "optiflow-0923.A5",
            shortId: "A5",
            title: "Bottleneck ladder with A$ per step on the capacity route (K2, P4)", // currency-ok: 23 Sep meeting action title, mirrors the dated actions file (pre-D26)
            state: "in-flight"
          }
        ]
      },
      {
        id: "optiflow-0923.A6",
        text: "Paul's downtime view shows where the downtime goes",
        actors: [
          "Paul"
        ],
        quote: "there's also one that I made, which is a downtime one. And that's really good at showing where's where's all the downtime",
        actions: [
          {
            id: "optiflow-0923.A6",
            shortId: "A6",
            title: "Downtime leads with impact: hours → tonnes → A$ (K3, P5)", // currency-ok: 23 Sep meeting action title, mirrors the dated actions file (pre-D26)
            state: "in-flight"
          }
        ]
      },
      {
        id: "optiflow-0923.A7",
        text: "The same predictive thinking on your equipment",
        actors: [
          "Greg"
        ],
        quote: "you can use this predictive modeling, not only on wash plants or processing plants. You can use these on your equipment.",
        actions: [
          {
            id: "optiflow-0923.A7",
            shortId: "A7",
            title: "BM01 bearing case through the same engine and pages (W3a–W3d, D16)",
            state: "in-flight"
          }
        ]
      }
    ]
  },
  {
    id: "paul-v2",
    date: "2026-09-23",
    title: "Paul module documents v2 — M1, M2, M3, M5, M12, M13 and the capacity-and-strategy screen",
    attendees: [
      "Paul",
      "Miles"
    ],
    inputPath: "docs/meetings/inputs/2026-09-23-paul-module-docs-v2.md",
    actionsPath: 'docs/meetings/2026-09-23-paul-module-docs-v2-actions.md',
    entries: [
    {
      id: "paul-v2.A5",
      text: "How hard can the plant run on today's ore",
      actors: ["Paul"],
      quote: "How hard can this plant run today, given the ore we are processing, and what is it worth to close the gap to the next constraint?",
      actions: [
        {
          id: "paul-v2.A2",
          shortId: "A2",
          title: "Engine: shared ore-grade state, circuit capacity, grade-driven NSR",
          state: "shipped",
          shippedIn: "v1.26",
          shippedCommit: "2c73722"
        },
        {
          id: "paul-v2.A5",
          shortId: "A5",
          title: "Capacity & strategy route, tab 1: Maximum throughput potential",
          state: "shipped",
          shippedIn: "v1.26",
          shippedCommit: "2c73722"
        }
      ]
    },
    {
      id: "paul-v2.A8",
      text: "Operate the plant for net value at today's prices",
      actors: ["Paul"],
      quote: "given today's metal prices and what we are mining, how should we operate this plant to maximise net value?",
      actions: [
        {
          id: "paul-v2.A8",
          shortId: "A8",
          title: "Tab 2: Value-optimised operating strategy",
          state: "planned"
        }
      ]
    },
    {
      id: "paul-v2.A1",
      text: "One consistent seed basis for capacity and strategy",
      actors: ["Paul", "Miles"],
      quote: "The baseline calibration uses the known utilisation at 57 stph and reference ore grades (Zn 6.8%, Pb 4.2%).",
      actions: [
        {
          id: "paul-v2.A1",
          shortId: "A1",
          title: "Seed basis: Paul's reference state; figure contradictions are recorded, not asked",
          state: "decided"
        }
      ]
    },
    {
      id: "paul-v2.A15",
      text: "A compound cause is counted once in the exposure",
      actors: ["Paul"],
      quote: "The financial impact is not the sum of three separate cases — it is calculated as the combined effect on Zn recovery, avoiding double-counting",
      actions: [
        {
          id: "paul-v2.A15",
          shortId: "A15",
          title: "Compound cause counted once; three-factor breakdown",
          state: "shipped",
          shippedIn: "v1.26",
          shippedCommit: "cd7cfb7"
        }
      ]
    },
    {
      id: "paul-v2.A9",
      text: "Throughput improvements take their benefit from the ceiling",
      actors: ["Paul"],
      quote: "Every throughput-related Module 13 Improvement Opportunity should reference this number as its benefit quantification.",
      actions: [
        {
          id: "paul-v2.A9",
          shortId: "A9",
          title: "M13 Step 2 capital case: Zn flotation capacity opportunity",
          state: "planned"
        }
      ]
    },
    {
      id: "paul-v2.A25",
      text: "Targets above the ceiling are logged, never hidden",
      actors: ["Paul"],
      quote: "this override is logged with the manager's name, timestamp, and stated reason. The advisory system continues to show the ceiling and the target separately",
      actions: [
        {
          id: "paul-v2.A25",
          shortId: "A25",
          title: "Ceiling override log and grade-change target review",
          state: "planned"
        }
      ]
    },
    {
      id: "paul-v2.A20",
      text: "Closure limits stratified by ore grade",
      actors: ["Paul"],
      quote: "Reconciliation closure control limits are not single fixed values — they are stratified by ore grade",
      actions: [
        {
          id: "paul-v2.A20",
          shortId: "A20",
          title: "Grade-stratified closure limits",
          state: "planned"
        }
      ]
    }
    ]
  },
  {
    id: "paul-tarp-0911",
    date: "2026-09-11",
    title: "Paul — control plan, TARP and FMEA strategy; mock walkthrough; Komatsu demo scope",
    attendees: [
      "Paul",
      "Miles"
    ],
    inputPath: "docs/meetings/inputs/2026-09-11-paul-tarp-control-plan.md",
    actionsPath: 'docs/meetings/2026-09-11-paul-tarp-control-plan-actions.md',
    entries: [
    {
      id: "paul-tarp-0911.A1",
      text: "Anonymise the mock as a lead-zinc concentrator for Komatsu",
      actors: ["Paul","Miles"],
      quote: "let's take any reference to Bunker Hill, but let's just call it a concentrator … we'll just call it the lead zinc concentrator",
      actions: [
        {
          id: "paul-tarp-0911.A1",
          shortId: "A1",
          title: "Anonymise the mock: every on-glass \"Bunker Hill\" becomes \"a lead-zinc concentrator\"",
          state: "decided"
        }
      ]
    },
    {
      id: "paul-tarp-0911.A7",
      text: "The FMEA is the textbook, the control plan the dictionary, the TARP the emergency card",
      actors: ["Paul"],
      quote: "it calls the FMEA the textbook and the control plan the dictionary and then the TARP the emergency card … do I dump the FMEA? It said no, it'd be good to have that FMEA as well",
      actions: [
        {
          id: "paul-tarp-0911.A7",
          shortId: "A7",
          title: "Textbook · dictionary · emergency card: the FMEA stays beside the control plan and the TARP",
          state: "decided"
        }
      ]
    },
    {
      id: "paul-tarp-0911.A9",
      text: "The rule set reads from the control plan and the TARP",
      actors: ["Paul","Miles"],
      quote: "this is where the control plan and the TARP would come into play. The control plan would specify that.",
      actions: [
        {
          id: "paul-tarp-0911.A9",
          shortId: "A9",
          title: "The rule set reads each target's band from the control plan, its causes from the FMEA, its ladder from the TARP",
          state: "planned"
        }
      ]
    },
    {
      id: "paul-tarp-0911.A10",
      text: "Control-plan bands follow the ore zone; nothing is written in stone",
      actors: ["Paul"],
      quote: "you might have a significant change in the ore because you're mining in a different zone, that froth height might have to change … we've got to be careful we don't write things in stone in the control plan",
      actions: [
        {
          id: "paul-tarp-0911.A10",
          shortId: "A10",
          title: "A control-plan band is tied to an ore zone; a zone change proposes a band change; safety limits are fixed",
          state: "planned"
        }
      ]
    },
    {
      id: "paul-tarp-0911.A12",
      text: "Split the metallurgist into senior met and control met",
      actors: ["Miles","Paul"],
      quote: "I think I'm gonna need to split that up into senior met and control room met. — Yeah, I think so. The more I think about it, there probably is that.",
      actions: [
        {
          id: "paul-tarp-0911.A12",
          shortId: "A12",
          title: "Split the metallurgist persona: senior metallurgist and control metallurgist",
          state: "decided"
        }
      ]
    },
    {
      id: "paul-tarp-0911.A18",
      text: "A first-party downtime module where the site has none",
      actors: ["Paul"],
      quote: "Bunker Hill, they probably couldn't afford to get a downtime module and we could create one",
      actions: [
        {
          id: "paul-tarp-0911.A18",
          shortId: "A18",
          title: "Reopen downtime coding as an OptiFlow-provided CRO entry screen and store?",
          state: "blocked"
        }
      ]
    },
    {
      id: "paul-tarp-0911.A20",
      text: "Which later modules to mock as screenshots for Komatsu",
      actors: ["Paul","Miles"],
      quote: "you could mock up a screenshot without even having the data in behind it … I'll have a bit of a think about some of those other screenshots and if we want to mock them up for Komatsu in 3 weeks' time",
      actions: [
        {
          id: "paul-tarp-0911.A20",
          shortId: "A20",
          title: "Which of modules 6–13 become screenshot-grade screens for the Komatsu demo",
          state: "blocked"
        }
      ]
    }
    ]
  },
  {
    id: "komatsu-demo-0911",
    date: "2026-09-11",
    title: "Komatsu — ProjeX + OptiFlow demo pre-brief (Jeremy Terpstra, MTS)",
    attendees: [
      "Greg",
      "Maiko",
      "External: Komatsu"
    ],
    inputPath: "docs/meetings/inputs/2026-09-11-komatsu-projex-optiflow-demo.md",
    actionsPath: 'docs/meetings/2026-09-11-komatsu-projex-optiflow-demo-actions.md',
    entries: [
      {
        id: "komatsu-demo-0911.A1",
        text: "A second audience read the recovery tile as the mine's cue to change the blend",
        actors: [
          "External: Komatsu",
          "Greg"
        ],
        quote: "maybe there's opportunities that we can send different material or different amounts of certain material to meet certain blending requirements based on actual recovery",
        actions: [
          {
            id: "komatsu-demo-0911.A1",
            shortId: "A1",
            title: "Mine-side blend feedback as a lens: a recovery or upset signal produces a blend or t/h nudge toward dispatch, seeded as a site variant",
            state: "planned"
          }
        ]
      },
      {
        id: "komatsu-demo-0911.A2",
        text: "The Komatsu strategy session opens on OptiFlow",
        actors: [
          "Greg"
        ],
        quote: "I think I might start with the OptiFlow and sort of say, this is sort of what it can look like, and then I'll jump back to ProjeX",
        actions: [
          {
            id: "komatsu-demo-0911.A2",
            shortId: "A2",
            title: "Komatsu strategy-session build-ahead note with a run-sheet section via /meeting prep",
            state: "decided"
          }
        ]
      }
    ]
  },
  {
    id: "optiflow-update-0909",
    date: "2026-09-09",
    title: "OptiFlow update — Greg, Paul, Tim, Nirman (internal)",
    attendees: [
      "Miles",
      "Paul",
      "Greg",
      "Tim",
      "Nirman"
    ],
    inputPath: "docs/meetings/inputs/2026-09-09-optiflow-update.md",
    actionsPath: 'docs/meetings/2026-09-09-optiflow-update-actions.md',
    entries: [
      {
        id: "optiflow-update-0909.A1",
        text: "The how is its own layer, never a sticker on the mock",
        actors: [
          "Nirman",
          "Miles"
        ],
        quote: "you assign we want a curve here, we want a chart here — how are you going to get that information, how will you compute it, where are your data sources going to be, what kind of formats … that's your schema but are you going to get that information and where are you going to get that information from",
        actions: [
          {
            id: "optiflow-update-0909.A1",
            shortId: "A1",
            title: "The how as its own layer: a data-sourcing foundations page and a `sources:` block per build doc — never a provenance sticker on the glass",
            state: 'shipped',
            shippedIn: 'v1.24',
            shippedCommit: 'a8bf99a'
          },
          {
            id: "optiflow-update-0909.A3",
            shortId: "A3",
            title: "Bunker Hill source-coverage calibration: which modules run on real sources, which are synthetic, what a first usable version contains",
            state: 'shipped',
            shippedIn: 'v1.24',
            shippedCommit: 'a8bf99a'
          }
        ]
      },
      {
        id: "optiflow-update-0909.A2",
        text: "The FMEA workbook is input and export, never the store",
        actors: [
          "Nirman"
        ],
        quote: "if someone changes the column name — they'll break it · you would isolate them to update versions on a specific folder and then you'll have a workflow … which sucks that in and transforms that into your data model … you wouldn't just be relying on that file to read directly on your app",
        actions: [
          {
            id: "optiflow-update-0909.A2",
            shortId: "A2",
            title: "FMEA ingest pipeline designed in the engine docs: versioned drop, drift-checked transform into rule claims, the rule set as the store, export back",
            state: 'shipped',
            shippedIn: 'v1.24',
            shippedCommit: 'a8bf99a'
          }
        ]
      },
      {
        id: "optiflow-update-0909.A4",
        text: "Roster-period view since the operator was last on",
        actors: [
          "Greg"
        ],
        quote: "the control operator comes in for his 12 hour shift and depending on their roster he might have been off for four days so you want that time period view … that would really add value to the control room operator or that metallurgist if they're on a roster pattern",
        actions: [
          {
            id: "optiflow-update-0909.A4",
            shortId: "A4",
            title: "Roster-period view: what changed since the CRO or metallurgist was last on shift",
            state: 'in-flight'
          }
        ]
      },
      {
        id: "optiflow-update-0909.A5",
        text: "Operating curve drifts from design by ore zone",
        actors: [
          "Greg",
          "Paul"
        ],
        quote: "once you get going you may find that the grade recovery curve is slightly different as you're operating … different zones in the mine … that's going to behave differently",
        actions: [
          {
            id: "optiflow-update-0909.A5",
            shortId: "A5",
            title: "Operating curve delta by ore zone; the bottleneck read carries the zone",
            state: 'in-flight'
          }
        ]
      },
      {
        id: "optiflow-update-0909.A6",
        text: "Rules retire when the investigation went off on a tangent",
        actors: [
          "Paul",
          "Miles"
        ],
        quote: "there may be things where they haven't found the true root cause and they've gone off on a tangent",
        actions: [
          {
            id: "optiflow-update-0909.A6",
            shortId: "A6",
            title: "Rule retirement is a real state with a stated reason",
            state: "decided"
          }
        ]
      },
      {
        id: "optiflow-update-0909.A7",
        text: "Three business cases; strip back before clients",
        actors: [
          "Greg"
        ],
        quote: "we give you transparency … we give you more accurate recovery curves depending on which section … we can give you a strategy per section of the mine … it's going to narrow down that reaction time",
        actions: [
          {
            id: "optiflow-update-0909.A7",
            shortId: "A7",
            title: "Value frame and convergence rule recorded: three business cases, grade–recovery focus, strip back before clients",
            state: "decided"
          }
        ]
      },
      {
        id: "ui-lift.C1",
        text: "UI lift a level above card moves, with an outside critic",
        actors: [
          "Miles"
        ],
        quote: "I'd like to do a big push with the user interface … at a level up from that, because obviously those tweaks just keep ticking on in the background",
        actions: [
          {
            id: "ui-lift.C1",
            shortId: "C1",
            title: "Consultant brief + one headless Codex critique of the Metallurgist landing, adjudicated; redesign candidates comped before code",
            state: "planned"
          }
        ]
      }
    ]
  },
  {
    id: "paul-fmea",
    date: "2026-09-09",
    title: "Paul / Miles call — FMEA workflow and metal accounting",
    attendees: [
      "Paul",
      "Miles"
    ],
    inputPath: "docs/meetings/inputs/2026-09-09-paul-fmea-workflow-call.md",
    actionsPath: 'docs/meetings/2026-09-09-paul-fmea-workflow-call-actions.md',
    entries: [
      {
        id: "paul-fmea.A3",
        text: "Live FMEA is an extract of approved rules",
        actors: [
          "Paul"
        ],
        quote: "I'm wondering whether it is a table that you can extract … it does make me think this becomes a live document that's sort of sitting there",
        actions: [
          {
            id: "paul-fmea.A3",
            shortId: "A3",
            title: "Live FMEA: the resolved rule set renders and exports in workbook shape after approval",
            state: 'shipped',
            shippedIn: 'v1.24',
            shippedCommit: 'b919823'
          }
        ]
      },
      {
        id: "paul-fmea.A4",
        text: "Workbook priority and alarm boundary replace the shortlist",
        actors: [
          "Paul"
        ],
        quote: "I have tried to do that, and it doesn't really come out. There is a priority thing that sits in here … a one, two, three from a priority. And there is a likelihood ranking … I don't think that needs to go to him because he's already going to have that alarm coming up",
        actions: [
          {
            id: "paul-fmea.A4",
            shortId: "A4",
            title: "Priority and likelihood from V8 onto rule claims; DCS-alarmed conditions never route to the CRO — closes the critical-parameter shortlist",
            state: 'shipped',
            shippedIn: 'v1.24',
            shippedCommit: '7cf84bc'
          }
        ]
      },
      {
        id: "paul-fmea.A10",
        text: "Accounting needs tails losses and a stage drill",
        actors: [
          "Paul"
        ],
        quote: "you'd also want to see what's going to tailings in this metal accounting screen, like percentage of zinc, lead, silver that's in tailings … that's an important number to see coming through",
        actions: [
          {
            id: "paul-fmea.A10",
            shortId: "A10",
            title: "Losses to tailings per element (Zn · Pb · Ag) on the layer strip and chain card",
            state: 'shipped',
            shippedIn: 'v1.24',
            shippedCommit: '244f595'
          }
        ]
      },
      {
        id: "paul-fmea.A14",
        text: "Superintendent tracks acceptance and time to act",
        actors: [
          "Paul"
        ],
        quote: "if I was a superintendent looking at my shift, I'd want to say what is AI telling my control room operator to do and my metallurgist to do, are they accepting those, and how fast are they taking that on board … it was suggesting things it should be raising as a super: you should raise this with the metallurgist or the control room operator to make sure there's some action happening",
        actions: [
          {
            id: "paul-fmea.A14",
            shortId: "A14",
            title: "Superintendent uptake read: recommendations by recipient role, accepted or rejected, time-to-act, and raise-with prompts",
            state: 'shipped',
            shippedIn: 'v1.24',
            shippedCommit: 'd55214a'
          }
        ]
      },
      {
        id: "paul-fmea.A15",
        text: "Smelter specifications become a plant setup input",
        actors: [
          "Paul"
        ],
        quote: "there's different elements that are penalty elements … mercury, fluorine, chlorine, cadmium, arsenic, antimony … I'd imagine you'd also have in here somewhere as another data sheet the actual quality spec that the smelter wants … if we're going to do a different plant we'd say send us the quality specifications that the downstream plant you're selling it to needs to meet",
        actions: [
          {
            id: "paul-fmea.A15",
            shortId: "A15",
            title: "Concentrate offtake specification as an input entity: smelter grade spec plus penalty-element trigger levels",
            state: 'shipped',
            shippedIn: 'v1.24',
            shippedCommit: 'e090bbd'
          }
        ]
      }
    ]
  },
  {
    id: "paul-feedback",
    date: "2026-09-09",
    title: "Paul screen feedback — role reads and recommendation tracking",
    attendees: [
      "Paul"
    ],
    inputPath: "docs/meetings/inputs/2026-09-09-paul-screen-feedback.md",
    actionsPath: 'docs/meetings/2026-09-09-paul-screen-feedback-actions.md',
    entries: [
      {
        id: "paul-feedback.A1",
        text: "The AI recommends and a named person acts",
        actors: [
          "Paul"
        ],
        quote: "With this AI system it will never make actual changes in the plant, it will make recommendations to the Metallurgist or control room operator or maintenance supervisor, this person then needs to either accept or reject the recommendation and if they accept then make the changes as suggested by AI",
        actions: [
          {
            id: "paul-feedback.A1",
            shortId: "A1",
            title: "Standing rule: the AI recommends, a named person accepts or rejects and implements",
            state: "decided"
          }
        ]
      },
      {
        id: "paul-feedback.A4",
        text: "Shift performance includes recovery and concentrate grade",
        actors: [
          "Paul"
        ],
        quote: "In the Shift performance table can we also add recovery and grade for Pb Zn & Ag, in my operations experience all shifts run the plant slightly differently and this creates either positive or negative consequences",
        actions: [
          {
            id: "paul-feedback.A4",
            shortId: "A4",
            title: "Superintendent: recovery and grade for Pb, Zn and Ag on the shift grid",
            state: 'shipped',
            shippedIn: 'v1.24',
            shippedCommit: 'f45e480'
          }
        ]
      },
      {
        id: "paul-feedback.A7",
        text: "Control room needs recovery trends and stage levers",
        actors: [
          "Paul"
        ],
        quote: "This is where AI can become a guide rather than just another fault or alarm that they have to accept or cancel. I would be trending things like overall recovery performance as this is a calculated variable. It would be also good to understand recovery performance and grade in each stage of the flotation circuits as they may be able to adjust reagent levels or air addition or froth height to improve",
        actions: [
          {
            id: "paul-feedback.A7",
            shortId: "A7",
            title: "Control room: overall recovery as a calculated trend and the flotation stage read with its levers",
            state: 'shipped',
            shippedIn: 'v1.24',
            shippedCommit: '5709318'
          }
        ]
      },
      {
        id: "paul-feedback.A8",
        text: "Reports track recommendation decisions and implementation",
        actors: [
          "Paul"
        ],
        quote: "It would be good for each shift for the daily report to specify what recommendations were suggested by AI, and if they were accepted or rejected and when were they implemented date and time of implementation",
        actions: [
          {
            id: "paul-feedback.A8",
            shortId: "A8",
            title: "Daily report: AI recommendations per shift with accept or reject and implementation time",
            state: 'shipped',
            shippedIn: 'v1.24',
            shippedCommit: 'c67e808'
          }
        ]
      }
    ]
  },
  {
    id: 'paul-call',
    date: '2026-09-04',
    title: 'Paul / Miles call — grade–recovery curve, two-level plant, intelligence layer',
    attendees: ['Paul', 'Miles'],
    inputPath: 'docs/meetings/inputs/2026-09-04-paul-grade-recovery-call.md',
    actionsPath: 'docs/meetings/2026-09-04-paul-grade-recovery-call-actions.md',
    entries: [
      {
        id: 'paul-call.A2',
        text: 'Two-level plant model: circuits you click into',
        actors: ['Paul'],
        quote:
          'at that higher level, it’s probably grinding. Lead flotation, zinc ' +
          'flotation. And then you double-click on the lead or the zinc ' +
          'flotation, it takes you down to another level. […] I don’t know ' +
          'whether you have red, green, amber dots on it saying, is that ' +
          'operating within spec?',
        actions: [
          {
            id: 'paul-call.A2',
            shortId: 'A2',
            title: 'Two-level plant model: circuits with red / amber / green, click into subprocesses (#252)',
            state: 'shipped',
            shippedIn: 'v1.22',
            shippedCommit: 'f8e9aa1',
          },
          {
            id: 'paul-call.A3',
            shortId: 'A3',
            title: 'First pass stops at subprocesses — mill + cyclone pack stays closed',
            state: 'decided',
          },
          {
            id: 'paul-call.A7',
            shortId: 'A7',
            title: 'Inside a circuit: off-parameter trends, same timestamp on the upstream circuit (#255)',
            state: 'shipped',
            shippedIn: 'v1.22',
            shippedCommit: 'f8e9aa1',
          },
        ],
      },
      {
        id: 'paul-call.A1',
        text: 'The working grade–recovery curve is an engine input',
        actors: ['Paul'],
        quote:
          'that’s probably an input into this process is we’d ask them for the ' +
          'grade recovery curve that they’re working off and actually give that ' +
          'to AI as a, I suppose, a mathematical equation. […] if you’re chasing ' +
          'recovery, this is what the grade should be, and it would be another ' +
          'alert to the system',
        actions: [
          {
            id: 'paul-call.A1',
            shortId: 'A1',
            title: 'Working grade–recovery curve is an engine input, not only a chart (#251)',
            state: 'shipped',
            shippedIn: 'v1.22',
            shippedCommit: 'e7a8631',
          },
        ],
      },
      {
        id: 'paul-call.A4',
        text: 'Process divergence reaches the CRO as a sample request',
        actors: ['Paul'],
        quote:
          'the alert that’s going to come to the control room operator isn’t ' +
          'going to be […] recovery’s out of spec. It’s going to be […] Tank ' +
          'levels high or pumpers tripped. […] Process. Divergence away from ' +
          'where it should be sitting. Is not really alerted anyway.',
        actions: [
          {
            id: 'paul-call.A4',
            shortId: 'A4',
            title: 'In-band divergence alerts as a sample request to the CRO (#253)',
            state: 'shipped',
            shippedIn: 'v1.22',
            shippedCommit: '75f47ec',
          },
        ],
      },
      {
        id: 'paul-call.A8',
        text: 'A plant variant with an on-stream analyser and faster samples',
        actors: ['Paul'],
        quote:
          'although we’re basing around Bunker Hill, I think we need to […] have ' +
          'a version that assumes there’s an on-stream analyzer going. And ' +
          'they’re getting samples at a lot higher frequency.',
        actions: [
          {
            id: 'paul-call.A8',
            shortId: 'A8',
            title: 'Sample-frequency plant variant: Bunker Hill lab lag vs on-stream analyser (#256)',
            state: 'shipped',
            shippedIn: 'v1.22',
            shippedCommit: '75f47ec',
          },
        ],
      },
      {
        id: 'paul-call.A6',
        text: 'FMEA function names the outcome: recovery, grade, or both',
        actors: ['Paul'],
        quote:
          'maybe that needs to be reworded slightly to talk about recovery and ' +
          'grade. […] it probably wasn’t explicit. […] in some examples, it ' +
          'could impact on both',
        actions: [
          {
            id: 'paul-call.A6',
            shortId: 'A6',
            title: 'FMEA function tags the outcome — recovery, grade, or both (#254)',
            state: 'planned',
          },
          {
            id: 'paul-call.A5',
            shortId: 'A5',
            title: 'Extend cause coverage: cyclone pack, media-through-grates, uncorrectable grind (folded into #250)',
            state: 'shipped',
            shippedIn: 'v1.22',
            shippedCommit: 'bfb511a',
          },
        ],
      },
    ],
  },
  {
    id: 'paul-email',
    date: '2026-09-04',
    title: 'Paul email — grade–recovery curve on the Metallurgist view',
    attendees: ['Paul', 'Miles'],
    inputPath: 'docs/meetings/inputs/2026-09-04-paul-grade-recovery-email.md',
    actionsPath: 'docs/meetings/2026-09-04-paul-grade-recovery-email-actions.md',
    entries: [
      {
        id: 'paul-email.A1',
        text: 'Keep the curve on the Metallurgist view as a seven-day record',
        actors: ['Paul'],
        quote:
          'I like what you did with the Grade recovery curve in the ' +
          'metallurgist view. This would be a good to show where the plant ' +
          'has been operating over a time period. maybe a daily achieved ' +
          'outcome displayed for the time period being reviewed.',
        actions: [
          {
            id: 'paul-email.A1',
            shortId: 'A1',
            title: 'The curve stays on the Metallurgist landing as a seven-day operating record — design point plus one achieved point per day; amends optiflow-update.A3 (#246)',
            state: 'shipped',
            shippedIn: 'v1.22',
            shippedCommit: 'e7a8631',
          },
        ],
      },
      {
        id: 'paul-email.A2',
        text: 'Below the curve, performance stage by stage',
        actors: ['Paul'],
        quote:
          'So although plotting this helps understand how the plant is ' +
          'performing it does not help the metallurgist understand what is ' +
          'happening to causes this loss in performance. […] My thoughts are ' +
          'yes we show where the plant is currently operating for the past 7 ' +
          'days, however below that we show performance across the plant',
        actions: [
          {
            id: 'paul-email.A2',
            shortId: 'A2',
            title: 'Stage ladder under the curve — grinding output, then each flotation stage as Readings with band and signal state (demo-critical, #247)',
            state: 'shipped',
            shippedIn: 'v1.22',
            shippedCommit: 'e7a8631',
          },
          {
            id: 'paul-email.A3',
            shortId: 'A3',
            title: 'Seed the stage variables the ladder reads, tagged to the plant graph (#248)',
            state: 'shipped',
            shippedIn: 'v1.22',
            shippedCommit: 'e7a8631',
          },
        ],
      },
      {
        id: 'paul-email.A4',
        text: 'The curve is a lab artefact from a small sample',
        actors: ['Paul'],
        quote:
          'The grade recovery curve is produced in a lab for a sample of the ' +
          'ore. As Greg indicated yesterday this sample is very small and ' +
          'will not represent the total ore body.',
        actions: [
          {
            id: 'paul-email.A4',
            shortId: 'A4',
            title: 'Record how the grade–recovery curve is produced and why the frontier stays off the glass (#249)',
            state: 'shipped',
            shippedIn: 'v1.22',
            shippedCommit: 'e7a8631',
          },
        ],
      },
      {
        id: 'paul-email.A5',
        text: 'Grinding and flotation causes as a rule-coverage check',
        actors: ['Paul'],
        quote:
          'Some of the issues in the grinding circuit that mean the target ' +
          'grind size include: Ore hardness has increased; Insufficient ' +
          'grinding media in the Ball mill; Grinding media size distribution; ' +
          'Mill grates worn or blocked; In accurate belt weigher meaning mill ' +
          'is overloaded with ore',
        actions: [
          {
            id: 'paul-email.A5',
            shortId: 'A5',
            title: 'Rule coverage against Paul’s grinding and flotation cause lists — uncovered causes enter as proposed FMEA rows (#250)',
            state: 'shipped',
            shippedIn: 'v1.22',
            shippedCommit: 'bfb511a',
          },
        ],
      },
    ],
  },
  {
    id: 'optiflow-update',
    date: '2026-09-03',
    title: 'OptiFlow update — excursion priority, FMEA, operating curve, build docs',
    attendees: ['Paul', 'Nirman', 'Miles'],
    inputPath: 'docs/meetings/inputs/2026-09-03-optiflow-update.md',
    actionsPath: 'docs/meetings/2026-09-03-optiflow-update-actions.md',
    entries: [
      {
        id: 'optiflow-update.A8',
        text: 'The demo hook is the Metallurgist role and journey',
        actors: ['Paul'],
        quote:
          'Paul was asked what the most important thing was as the hook for ' +
          'demoing. Essentially, Paul’s response was it was the Metallurgist ' +
          'role and that user journey.',
        actions: [
          {
            id: 'optiflow-update.A8',
            shortId: 'A8',
            title: 'The demo hook is the Metallurgist role and journey — reaffirms the demo brief (aug26.A10 / #186); Met landing is the demo’s first screen',
            state: 'decided',
          },
        ],
      },
      {
        id: 'optiflow-update.A4',
        text: 'Metallurgist reads cause and what to change, not the curve',
        actors: ['Miles'],
        quote:
          'Operating curve. Could be useful for a superintendent but not useful ' +
          'for a metallurgist. Seeing that graph doesn’t mean much to a ' +
          'metallurgist, they would care about the cause and what needs to be ' +
          'changed.',
        actions: [
          {
            id: 'optiflow-update.A4',
            shortId: 'A4',
            title: 'Metallurgist landing leads with cause and what to change (demo-critical, #241)',
            state: 'shipped',
            shippedIn: 'v1.21',
            shippedCommit: '4429ee7',
          },
          {
            id: 'optiflow-update.A3',
            shortId: 'A3',
            title: 'Operating curve moves to the Superintendent landing (#243)',
            state: 'shipped',
            shippedIn: 'v1.21',
            shippedCommit: '4429ee7',
          },
        ],
      },
      {
        id: 'optiflow-update.A7',
        text: 'No operating-curve math at Bunker Hill',
        actors: ['Miles'],
        quote:
          'There was discussion around the actual operating curve math and this ' +
          'source is unavailable — Bunker Hill didn’t even have these.',
        actions: [
          {
            id: 'optiflow-update.A7',
            shortId: 'A7',
            title: 'No operating-curve math at Bunker Hill — the frontier stays off the glass; the panel is a position map',
            state: 'decided',
          },
        ],
      },
      {
        id: 'optiflow-update.A1',
        text: 'Excursion events need a priority, not an alarm flood',
        actors: ['Miles'],
        quote: 'Priority of excursion events, don’t want to overload with alarms.',
        actions: [
          {
            id: 'optiflow-update.A1',
            shortId: 'A1',
            title: 'Excursion priority — alert governance bounds the feed, never the limits (#239)',
            state: 'shipped',
            shippedIn: 'v1.21',
            shippedCommit: '47678fe',
          },
        ],
      },
      {
        id: 'optiflow-update.A2',
        text: 'Is FMEA the right knowledge form for an AI-driven loop?',
        actors: ['Miles'],
        quote:
          'Is FMEA the best? Open question — perhaps explore other options that ' +
          'are better suited for AI.',
        actions: [
          {
            id: 'optiflow-update.A2',
            shortId: 'A2',
            title: 'Is FMEA the right authored knowledge form for the advisory loop? (#240, QnA 2026-09-03 Q1)',
            state: 'decided',
          },
        ],
      },
      {
        id: 'optiflow-update.A6',
        text: 'Build docs must come out of meetings, and ahead of promises',
        actors: ['Nirman', 'Miles'],
        quote:
          'There needs to be ongoing build documentation thinking about how ' +
          'things will actually be built in advance, otherwise you might make ' +
          'promises around functionality that might actually be a much bigger ' +
          'lift than you anticipate.',
        actions: [
          {
            id: 'optiflow-update.A5',
            shortId: 'A5',
            title: 'Build docs are an output of the meeting chain, not an afterthought (#242)',
            state: 'shipped',
            shippedIn: 'v1.21',
            shippedCommit: 'c5e890a',
          },
          {
            id: 'optiflow-update.A6',
            shortId: 'A6',
            title: 'Build-ahead notes — lift assessed before a capability is promised (#244)',
            state: 'shipped',
            shippedIn: 'v1.21',
            shippedCommit: 'c5e890a',
          },
        ],
      },
    ],
  },
  {
    id: 'aug27',
    date: '2026-08-27',
    title: 'Weekly dev catch-up — OptiFlow direction after Paul’s v19',
    attendees: ['Tim', 'Nirman', 'Miles'],
    inputPath: 'docs/meetings/inputs/2026-08-27-dev-catchup.md',
    // No actions file: the meeting's outputs were routed straight into the
    // spec-vs-actual reconciliation (docs/plans/spec-vs-actual-mapping.md).
    actionsPath: 'docs/plans/spec-vs-actual-mapping.md',
    entries: [
      {
        id: 'aug27.D1',
        text: 'Design spec versus actual is the product’s central question',
        actors: ['Tim', 'Nirman'],
        quote:
          'This is the delta between the design spec and the actual, which is ' +
          'the key piece. This is the missing gap.',
        actions: [
          {
            id: 'aug27.D1',
            shortId: 'D1',
            title: 'One numeric state model with a time axis — every plant quantity a Reading with design · target · band · provenance (specvactual.B4 / #196)',
            state: 'shipped',
            shippedIn: 'v1.20',
            shippedCommit: 'ea2e4cf',
          },
        ],
      },
      {
        id: 'aug27.D2',
        text: 'Pursue a two-dimensional operating curve with a time trend',
        actors: ['Tim', 'Miles'],
        quote:
          'We can all picture some sort of process system which translates itself ' +
          'to a two-dimensional operating curve, where there’s somewhere you’re ' +
          'moving along that curve or moving along a different curve… the ' +
          'original spec and where we’re at.',
        actions: [
          {
            id: 'aug27.D2',
            shortId: 'D2',
            title: 'Operating-curve panel: grade–recovery position map with trail, now and what-if (specvactual.B7 / #199, B7b / #214)',
            state: 'shipped',
            shippedIn: 'v1.20',
            shippedCommit: 'ea2e4cf',
          },
        ],
      },
      {
        id: 'aug27.D3',
        text: 'Deep on modules 1 and 3; stop mocking for breadth',
        actors: ['Miles', 'Tim'],
        quote:
          'If the time is better spent going really deep on one and three, then I ' +
          'feel like I need to make that decision before going too broad.',
        actions: [
          {
            id: 'aug27.D3',
            shortId: 'D3',
            title: 'Role-scoped rail — the phase-1 set only, per persona (specvactual.B3 / #195)',
            state: 'shipped',
            shippedIn: 'v1.20',
            shippedCommit: '15c1a4f',
          },
        ],
      },
      {
        id: 'aug27.D4',
        text: 'Module 14 is a backend engine other modules consume',
        actors: ['Miles'],
        quote:
          'I had to read it — it is really just back end, but it will inform ' +
          'what the other modules look like.',
        actions: [
          {
            id: 'aug27.D4',
            shortId: 'D4',
            title: 'SPC + diagnosis engines: computed limits, four rules, one ExcursionEvent stream, matcher and gate (specvactual.B6 / #198)',
            state: 'shipped',
            shippedIn: 'v1.20',
            shippedCommit: 'ea2e4cf',
          },
        ],
      },
      {
        id: 'aug27.D5',
        text: 'Paul’s v19 module document is the source of truth',
        actors: ['Tim', 'Miles'],
        quote:
          'I would definitely take this as the source of truth now — this is the ' +
          'latest thing.',
        actions: [
          {
            id: 'aug27.D5',
            shortId: 'D5',
            title: 'v19 is the authority the roadmap and issue reconciliation run against',
            state: 'decided',
          },
          {
            id: 'aug27.D6',
            shortId: 'D6',
            title: 'What does v19 supersede in the Bunker Hill proposal? (open question; build-ahead notes #244 close the gap)',
            state: 'blocked',
          },
        ],
      },
      {
        id: 'aug27.D7',
        text: 'The plant overview is a process graph above the dashboards',
        actors: ['Nirman', 'Tim'],
        quote:
          'That whole document is full of dashboards, but what they want is not ' +
          'that. They want an overview of the plant and they want to see the ' +
          'constraints, where that is. But when you click on something, they want ' +
          'to look at what’s going wrong with it, as progressive discovery.',
        actions: [
          {
            id: 'aug27.D7',
            shortId: 'D7',
            title: 'Plant model on React Flow, 2D, node click drills into the role screen for that stage (specvactual.B2 / #194)',
            state: 'shipped',
            shippedIn: 'v1.20',
            shippedCommit: 'f396a90',
          },
          {
            id: 'aug27.D8',
            shortId: 'D8',
            title: 'Overview landings aligned to Paul’s §8 performance screens — his dashboards as what you drill into (specvactual.B1b / #203)',
            state: 'shipped',
            shippedIn: 'v1.20',
            shippedCommit: 'db116a8',
          },
        ],
      },
      {
        id: 'aug27.D9',
        text: 'Codifying the knowledge layer is its own strategic arc',
        actors: ['Tim'],
        quote:
          'This whole strategic arc around how are we really going to start ' +
          'codifying this knowledge layer, because it’s starting to become such a ' +
          'big piece for us now.',
        actions: [
          {
            id: 'aug27.D9',
            shortId: 'D9',
            title: 'Plant model construction thinking piece — sources → typed claims → versioned plant model; fifteen follow-ups construct.R1–R15 (#221–#235)',
            state: 'in-flight',
          },
        ],
      },
    ],
  },
  {
    id: 'paul',
    date: '2026-08-26',
    title: 'OptiFlow UI discussion — Paul’s module priorities',
    attendees: ['Paul', 'Greg', 'Tim', 'Nirman', 'Miles'],
    inputPath: 'docs/meetings/inputs/2026-08-26-paul-module-priorities.md',
    actionsPath: 'docs/meetings/2026-08-26-paul-module-priorities-actions.md',
    entries: [
      {
        id: 'paul.A1',
        text: 'Phase 1 is modules 1, 3 and 14',
        actors: ['Paul'],
        quote:
          'For me, Bunker Hill is modules one, three, and probably 13. … I think ' +
          'we should definitely focus around this Foundation. Definitely. I’m ' +
          'with that.',
        actions: [
          {
            id: 'paul.A1',
            shortId: 'A1',
            title: 'Phase-1 module set is M1 + M3 + M14; M2 crossed, M13 a separate arc, M6 last, M9 undesigned',
            state: 'decided',
          },
          {
            id: 'paul.A4',
            shortId: 'A4',
            title: 'M2 deprioritised — keep what shipped, take it off the rail (specvactual.B3)',
            state: 'shipped',
            shippedIn: 'v1.20',
            shippedCommit: '15c1a4f',
          },
        ],
      },
      {
        id: 'paul.A10',
        text: 'The metallurgist’s four hours is the demo hook',
        actors: ['Paul'],
        quote:
          'A metallurgist will spend four hours analyzing what happened over the ' +
          'last 24 hours. And then what does that look like over the week? And ' +
          'updating Excel spreadsheets.',
        actions: [
          {
            id: 'paul.A10',
            shortId: 'A10',
            title: 'Demo brief — the metallurgist’s four hours as the opening hook (#186; reaffirmed 2026-09-03)',
            state: 'shipped',
            shippedIn: 'v1.21',
            shippedCommit: '6ce7d58',
          },
        ],
      },
      {
        id: 'paul.A7',
        text: 'SPC engine — drift inside tolerance made visible',
        actors: ['Paul'],
        quote:
          'The whole SPC module is around saying, well, right here, I’ve got a ' +
          'divergence away from the set point… if we didn’t do that, it would ' +
          'drift and we wouldn’t know until it’s out of spec.',
        actions: [
          {
            id: 'paul.A7',
            shortId: 'A7',
            title: 'SPC engine (M14) as a backend engine beside the FMEA comparison (specvactual.B6 / #198)',
            state: 'shipped',
            shippedIn: 'v1.20',
            shippedCommit: 'ea2e4cf',
          },
          {
            id: 'paul.A9',
            shortId: 'A9',
            title: 'Control chart component — in-tolerance drift on the glass (B6 consumers)',
            state: 'shipped',
            shippedIn: 'v1.20',
            shippedCommit: 'ea2e4cf',
          },
        ],
      },
      {
        id: 'paul.A8',
        text: 'A central hub home — tracking against spec, what is drifting',
        actors: ['Paul'],
        quote:
          'I think it’s clear we have this central sort of hub home where it’s a ' +
          'visual way of showing how we’re tracking against spec and what’s ' +
          'drifting. And then, in a sense, a lot of these modules, some of it’s ' +
          'unpacking that.',
        actions: [
          {
            id: 'paul.A8',
            shortId: 'A8',
            title: 'Overview landings re-cut to Paul’s §8 performance screens with the drift read (specvactual.B1b / #203)',
            state: 'shipped',
            shippedIn: 'v1.20',
            shippedCommit: 'db116a8',
          },
        ],
      },
      {
        id: 'paul.A6',
        text: 'The FMEA workshop at Bunker Hill drives the advisory engine',
        actors: ['Paul'],
        quote:
          'There’s going to be a workshop… the first time we go over there, ' +
          'we’re going to sit there and pull that FMEA apart with them, because ' +
          'then that’s going to drive the engine for us around their processing ' +
          'plant.',
        actions: [
          {
            id: 'paul.A6',
            shortId: 'A6',
            title: 'FMEA is a configurable client input, not baked-in rules — workshop dependency recorded',
            state: 'decided',
          },
          {
            id: 'paul.A11',
            shortId: 'A11',
            title: 'Critical-parameter shortlist — which parameters carry the read (B5 seeded an 18-series stand-in)',
            state: 'blocked',
          },
        ],
      },
      {
        id: 'paul.A2',
        text: 'Integrate with the adjacent product, never rebuild it',
        actors: ['Paul'],
        quote:
          'You want to avoid the arc of building a monitoring system, but you just ' +
          'want to link it to any existing tool or platform they use or a database ' +
          'they use. And we just get it for the sake of intelligence, not making a ' +
          'new platform for monitoring systems.',
        actions: [
          {
            id: 'paul.A2',
            shortId: 'A2',
            title: 'Scope-boundary principle page (docs/foundations/scope-boundary.md, #201)',
            state: 'shipped',
            shippedIn: 'v1.20',
            shippedCommit: '024cd9e',
          },
        ],
      },
      {
        id: 'paul.A12',
        text: 'The functional description is the artefact behind the data model',
        actors: ['Paul'],
        quote:
          'The functional description gives a lot of that info that we’re after ' +
          'to drive this… the functional description is what we’re probably ' +
          'really after that helps us describe that.',
        actions: [
          {
            id: 'paul.A12',
            shortId: 'A12',
            title: 'Functional description as the ingestion artefact behind the data model',
            state: 'planned',
          },
          {
            id: 'paul.A14',
            shortId: 'A14',
            title: 'Ingest Paul’s functional descriptions when they arrive (v19 landed; two still outstanding)',
            state: 'blocked',
          },
        ],
      },
      {
        id: 'paul.A20',
        text: 'Module 9 (maintenance) is needed but undesigned',
        actors: ['Paul'],
        quote:
          'I haven’t really thought through module nine, but I think at some ' +
          'stage you need a module nine.',
        actions: [
          {
            id: 'paul.A20',
            shortId: 'A20',
            title: 'M9 maintenance module shape — freeze the shipped surface, off the rail, until Paul re-endorses',
            state: 'blocked',
          },
        ],
      },
    ],
  },
  {
    id: 'greg',
    date: '2026-08-26',
    title: 'Greg / Nirman / Miles / Tim — feedback on OptiFlow mock v1',
    attendees: ['Greg', 'Tim', 'Nirman', 'Miles'],
    inputPath: 'docs/meetings/inputs/2026-08-26-greg-optiflow-feedback.md',
    actionsPath: 'docs/meetings/2026-08-26-greg-optiflow-feedback-actions.md',
    entries: [
      {
        id: 'greg.G2',
        text: 'Design curve → operating curve is the product thesis',
        actors: ['Greg'],
        quote:
          'That’s really the key of dialling a plant in — you go from this ' +
          'design state, which is a theory, to actual practice. … When we get ' +
          'variances from that line … it can highlight that as a problem, because ' +
          'if you’re out of spec.',
        actions: [
          {
            id: 'greg.G2',
            shortId: 'G2',
            title: 'Design-vs-actual as one numeric state model; the operating-curve panel as its surface form (specvactual.B4, B7)',
            state: 'shipped',
            shippedIn: 'v1.20',
            shippedCommit: 'ea2e4cf',
          },
          {
            id: 'greg.G3',
            shortId: 'G3',
            title: 'Three plant states — design · operating · future — drawn as design point, trail + now, what-if arrow (B7 / B7b)',
            state: 'shipped',
            shippedIn: 'v1.20',
            shippedCommit: 'ea2e4cf',
          },
        ],
      },
      {
        id: 'greg.G6',
        text: 'Rising kW is not only hardness — rules need a mechanical branch',
        actors: ['Greg'],
        quote:
          'When you start talking about kilowatts you’re saying hardness and ' +
          'these sorts of things — but that could also mean it could be something ' +
          'wrong with bearings, could be something along the shaft … so there’s a ' +
          'process flag and then there’s a mechanical flag.',
        actions: [
          {
            id: 'greg.G6',
            shortId: 'G6',
            title: 'Structured DiagnosticRule with causeCategory process-vs-mechanical and a rival (specvactual.B6 / #47)',
            state: 'shipped',
            shippedIn: 'v1.20',
            shippedCommit: 'ea2e4cf',
          },
        ],
      },
      {
        id: 'greg.G8',
        text: 'A GM never sees the process plant — role depth boundaries',
        actors: ['Greg'],
        quote:
          'As a GM level they would never see the process plant, what’s happening ' +
          '— correct, ever. They wouldn’t even know what the hell is going on. Nor ' +
          'would the operator understand the cost models that go behind it.',
        actions: [
          {
            id: 'greg.G8',
            shortId: 'G8',
            title: 'Role axis on the rail — the GM never sees the plant model (specvactual.B3 / #195)',
            state: 'shipped',
            shippedIn: 'v1.20',
            shippedCommit: '15c1a4f',
          },
        ],
      },
      {
        id: 'greg.G9',
        text: 'Hundreds of flags — the value is the weighting, not the detection',
        actors: ['Greg'],
        quote:
          'Playing with how material the issue is as well … those flags become ' +
          'some sort of risk-weighted sort of process — that’s probably a good ' +
          'way to do it. How do you flag them, because you could have hundreds ' +
          'sometimes.',
        actions: [
          {
            id: 'greg.G9',
            shortId: 'G9',
            title: 'Materiality-weighted flag triage — became excursion priority / alert governance (optiflow-update.A1 / #239)',
            state: 'planned',
          },
        ],
      },
      {
        id: 'greg.G12',
        text: 'Surface the right thing at the right time — details underneath',
        actors: ['Greg', 'Tim'],
        quote:
          'It’s pretty overwhelming … surface in an intelligent way, the right ' +
          'thing at the right time for the right person, but then all the details ' +
          'are underneath it as you need it — as opposed to the traditional ' +
          'dashboard view of flashing lights and buttons.',
        actions: [
          {
            id: 'greg.G12',
            shortId: 'G12',
            title: 'Progressive discovery pass — the disclosure ladder Plant → Node → Signal → Case → FMEA row (aug26.A12 / #187)',
            state: 'planned',
          },
        ],
      },
      {
        id: 'greg.G1',
        text: 'Pin the top five problems OptiFlow solves, with Paul',
        actors: ['Greg'],
        quote:
          'I just want to get back to you guys and say, what is the top five ' +
          'things that we’re trying to solve for? It’s all here — but what’s ' +
          'really … top five, top ten, don’t care.',
        actions: [
          {
            id: 'greg.G1',
            shortId: 'G1',
            title: 'Top five problems OptiFlow solves — Greg convenes with Paul',
            state: 'blocked',
          },
          {
            id: 'greg.G5',
            shortId: 'G5',
            title: 'Reconcile the mock with the Bunker Hill proposal (what v19 supersedes — still open)',
            state: 'blocked',
          },
        ],
      },
      {
        id: 'greg.G4',
        text: 'Statistical models underneath; the AI layer judges on top',
        actors: ['Greg'],
        quote:
          'You would need the statistical modelling behind it, wouldn’t you? … ' +
          'Now we’re crossing over where Pinnacle is and where this is as well.',
        actions: [
          {
            id: 'greg.G4',
            shortId: 'G4',
            title: 'Statistical-model layer and the Pinnacle boundary (progressing-optiflow.A5 / #15)',
            state: 'blocked',
          },
        ],
      },
      {
        id: 'greg.G11',
        text: 'A shareable mock link for Greg and Paul',
        actors: ['Greg'],
        quote:
          'Can we share this with Paul or not? … We might deploy this on mock ' +
          'node and we can have a link, Greg, for you and Paul to review.',
        actions: [
          {
            id: 'greg.G11',
            shortId: 'G11',
            title: 'Deploy a shareable mock link — the fork’s Cloudflare deploy (fork.B1 / #1)',
            state: 'in-flight',
          },
        ],
      },
    ],
  },
  {
    id: 'aug26',
    date: '2026-08-26',
    title: 'Miles / Nirman / Tim — OptiFlow mock review',
    attendees: ['Tim', 'Nirman', 'Miles'],
    inputPath: 'docs/meetings/inputs/2026-08-26-optiflow-mock-review.md',
    actionsPath: 'docs/meetings/2026-08-26-optiflow-mock-review-actions.md',
    entries: [
      {
        id: 'aug26.A14',
        text: 'User journeys were never captured — refinement has no criterion',
        actors: ['Nirman'],
        quote:
          'Pick a role — don’t answer for five roles, pick a role, start with ' +
          'the GM. Put yourself as a GM and ask: what would I do if I’m running a ' +
          'plant, and what do I need to know?',
        actions: [
          {
            id: 'aug26.A14',
            shortId: 'A14',
            title: 'User-journey capture programme, starting with the GM (#188)',
            state: 'planned',
          },
          {
            id: 'aug26.A15',
            shortId: 'A15',
            title: 'Weave the modules vertically along the journeys (#189)',
            state: 'planned',
          },
        ],
      },
      {
        id: 'aug26.A7',
        text: 'Every role view answers status → critical issues → actions',
        actors: ['Tim'],
        quote:
          'It’s a concept for every role that’s kind of: what’s current status ' +
          'against the BAU expected, what are critical issues that I need to know ' +
          'about, and are there any other actions — whether they be BAU or ' +
          'exceptions — that I need to do.',
        actions: [
          {
            id: 'aug26.A7',
            shortId: 'A7',
            title: 'Role views answer status → critical issues → actions — the acceptance frame for a landing (#183; Route reads arc)',
            state: 'shipped',
            shippedIn: 'v1.21',
            shippedCommit: '4429ee7',
          },
        ],
      },
      {
        id: 'aug26.A3',
        text: 'Bottleneck → root cause must be a click path, not navigation',
        actors: ['Tim'],
        quote:
          'I’ve got the grind and that looks like the stress and bottleneck. I ' +
          'want to know what is the root cause of it — can I click on it?',
        actions: [
          {
            id: 'aug26.A3',
            shortId: 'A3',
            title: 'Bottleneck → root-cause drill-down — the disclosure ladder Plant → Node → Case',
            state: 'shipped',
            shippedIn: 'v1.21',
            shippedCommit: 'f396a90',
          },
          {
            id: 'aug26.A6',
            shortId: 'A6',
            title: 'Surface how the bottleneck determination is derived — computed utilisation and node tone (specvactual.B4 / #182)',
            state: 'shipped',
            shippedIn: 'v1.20',
            shippedCommit: 'ea2e4cf',
          },
        ],
      },
      {
        id: 'aug26.A1',
        text: 'Project home is a thin hub — the journey starts there',
        actors: ['Tim'],
        quote:
          'It’s a natural starting point, home. This is the level you want to ' +
          'start with — what this is telling me is that it’s a hub for modules.',
        actions: [
          {
            id: 'aug26.A1',
            shortId: 'A1',
            title: 'Project home is the hub — the Plant rung (specvactual.B1b + B2, #177)',
            state: 'shipped',
            shippedIn: 'v1.20',
            shippedCommit: 'db116a8',
          },
          {
            id: 'aug26.A4',
            shortId: 'A4',
            title: 'Process graph — illustrative vs product: product, on React Flow (specvactual.B2 / #180)',
            state: 'shipped',
            shippedIn: 'v1.20',
            shippedCommit: 'f396a90',
          },
        ],
      },
      {
        id: 'aug26.A12',
        text: 'Rule of three, applied progressively',
        actors: ['Nirman'],
        quote:
          'A rule of three works really well in anything you put together — ' +
          'people love low, medium, high … the moment you’re trying to tell the ' +
          'user more than five things, they’ll be lost … however, if you follow ' +
          'that blindly you end up in a position where everything looks minimal ' +
          'and it’s not detailed enough — so the rule of three should go ' +
          'progressively.',
        actions: [
          {
            id: 'aug26.A12',
            shortId: 'A12',
            title: 'Record rule-of-three + progressive disclosure as a named design rule in DESIGN.md (#187)',
            state: 'shipped',
            shippedIn: 'v1.21',
            shippedCommit: 'c5e890a',
          },
          {
            id: 'aug26.A8',
            shortId: 'A8',
            title: 'Metal accounting is too busy — progressive-disclosure pass (#184; parked with M2)',
            state: 'shipped',
            shippedIn: 'v1.21',
            shippedCommit: 'c5d8635',
          },
        ],
      },
      {
        id: 'aug26.A10',
        text: 'The demo opens at the portfolio, then project home',
        actors: ['Tim'],
        quote:
          'Start with this, because it’s just good reinforcement — we’re having ' +
          'this conversation with the broader team that we’ve now got foundation ' +
          'to build on, so it’s a good way to say look, this is just another style ' +
          'of project.',
        actions: [
          {
            id: 'aug26.A10',
            shortId: 'A10',
            title: 'Demo brief — portfolio → project home → module; stock answers folded in (#186)',
            state: 'shipped',
            shippedIn: 'v1.21',
            shippedCommit: '6ce7d58',
          },
          {
            id: 'aug26.A11',
            shortId: 'A11',
            title: 'Ingestion named as where the specced half comes from, not walked through (#186)',
            state: 'shipped',
            shippedIn: 'v1.21',
            shippedCommit: '6ce7d58',
          },
        ],
      },
    ],
  },
  {
    id: 'tim-hook',
    date: '2026-08-21',
    title: 'Tim email — demo killer hook + screen inventory',
    attendees: ['Tim', 'Miles'],
    inputPath: 'docs/meetings/inputs/2026-08-21-tim-demo-hook.md',
    actionsPath: 'docs/meetings/2026-08-21-tim-demo-hook-actions.md',
    entries: [
      {
        id: 'tim-hook.A1',
        text: 'The live process model is the demo’s killer hook',
        actors: ['Tim'],
        quote:
          'A UI screen that showed a visual view of the live process model … ' +
          'dynamically change various inputs and watch various outputs change ' +
          '(e.g. bubbles move through the process that change their size or ' +
          'speed with the value).',
        actions: [
          {
            id: 'tim-hook.A1',
            shortId: 'A1',
            title: 'Live process model — §8 topology graph, animated flow, what-if sliders drive the graph (#162)',
            state: 'shipped',
            shippedIn: 'v1.17',
            shippedCommit: '5e9716d',
          },
          {
            id: 'tim-hook.A2',
            shortId: 'A2',
            title: 'Explicit non-left-to-right flow topology between plant-model nodes (#110)',
            state: 'shipped',
            shippedIn: 'v1.17',
            shippedCommit: '0b6ee18',
          },
        ],
      },
      {
        id: 'tim-hook.A3',
        text: 'Layer metal accounting over the live view',
        actors: ['Tim'],
        quote: 'You can then layer the metal accounting (module 2) over it, etc.',
        actions: [
          {
            id: 'tim-hook.A3',
            shortId: 'A3',
            title: 'Metal-accounting layer on the plant model + balance-network reference (#163)',
            state: 'shipped',
            shippedIn: 'v1.18',
            shippedCommit: '3b9d8f7',
          },
        ],
      },
      {
        id: 'tim-hook.A5',
        text: 'Four inventory screens were missing — build them as screens',
        actors: ['Tim', 'Miles'],
        quote:
          'Risk register / interlock table (Module 6 §4), including the ' +
          'override-authority visibility Section 4 there calls out as important.',
        actions: [
          {
            id: 'tim-hook.A5',
            shortId: 'A5',
            title: 'Risk register / interlock table screen (/risk, #164)',
            state: 'shipped',
            shippedIn: 'v1.18',
            shippedCommit: '1aabbac',
          },
          {
            id: 'tim-hook.A7',
            shortId: 'A7',
            title: 'Cost & revenue + Improvement Opportunity register — one screen, two tabs (/cost, #165)',
            state: 'shipped',
            shippedIn: 'v1.18',
            shippedCommit: '1aabbac',
          },
          {
            id: 'tim-hook.A6',
            shortId: 'A6',
            title: 'Operator round sheet / task checklist — the shop-floor screen (aug21.B1 / #167)',
            state: 'shipped',
            shippedIn: 'v1.18',
            shippedCommit: 'ef17dd0',
          },
        ],
      },
      {
        id: 'tim-hook.A4',
        text: 'Revisit the C1 §7 inventory against the set Greg had',
        actors: ['Tim'],
        quote: 'Be good to just revisit that with the set Greg had.',
        actions: [
          {
            id: 'tim-hook.A4',
            shortId: 'A4',
            title: 'C1 §7 inventory ↔ route reconciliation recorded in the realignment register §4',
            state: 'shipped',
            shippedIn: 'v1.16',
            shippedCommit: '1b0d962',
          },
        ],
      },
    ],
  },
  {
    id: 'aug20',
    date: '2026-08-20',
    title: 'Weekly dev team catch-up',
    attendees: ['Tim', 'Nirman', 'Miles'],
    inputPath: 'docs/meetings/inputs/2026-08-20-dev-catchup.md',
    actionsPath: 'docs/meetings/2026-08-20-dev-catchup-actions.md',
    entries: [
      {
        id: 'aug20.A13',
        text: 'Metal accounting (module 2) is next',
        actors: ['Tim'],
        quote:
          'Metal accounting was definitely one of the key ones Paul saw as part ' +
          'of the first batch \u2026 I think that could be very compelling how you ' +
          'show that with the various graphics.',
        actions: [
          {
            id: 'aug20.A13',
            shortId: 'A13',
            title: 'Metal accounting module (module 2) \u2014 build next (reconcile.T11 / #50 pulled forward)',
            state: 'shipped',
            shippedIn: 'v1.12',
            shippedCommit: 'b6b1887',
          },
        ],
      },
      {
        id: 'aug20.A8',
        text: 'Money-path walkthrough is what the demo needs',
        actors: ['Miles'],
        quote:
          'The place I want to take it further is the user journey itself \u2014 the ' +
          'walkthrough across all six or seven roles, a scripted walkthrough, ' +
          'because that\u2019s what you want for the demo.',
        actions: [
          {
            id: 'aug20.A8',
            shortId: 'A8',
            title: 'Money-path scripted walkthrough across the roles (mirror of reconcile.T7 / #46; demo-critical for 26 Aug)',
            state: 'shipped',
            shippedIn: 'v1.13',
            shippedCommit: 'a707f6a',
          },
        ],
      },
      {
        id: 'aug20.A10',
        text: 'Plant model: keep the depth, make it opt-in',
        actors: ['Nirman'],
        quote:
          'Do not dial the information down \u2014 make it collapsible/expandable so ' +
          'that when the user wants to know more they can learn more. Low medium ' +
          'high, green amber red \u2014 then click on it and know why.',
        actions: [
          {
            id: 'aug20.A10',
            shortId: 'A10',
            title: 'Plant-model progressive disclosure \u2014 collapsible sections, summary vs detail, RAG + why (#109)',
            state: 'shipped',
            shippedIn: 'v1.14',
            shippedCommit: 'f1a9e26',
          },
          {
            id: 'aug20.A11',
            shortId: 'A11',
            title: 'Left-to-right interconnection on the top process strip (#110)',
            state: 'shipped',
            shippedIn: 'v1.14',
            shippedCommit: 'f1a9e26',
          },
          {
            id: 'aug20.A12',
            shortId: 'A12',
            title: 'Plant-model in-page wayfinding \u2014 sub-tabs / back-to-top (#111)',
            state: 'shipped',
            shippedIn: 'v1.14',
            shippedCommit: 'f1a9e26',
          },
        ],
      },
      {
        id: 'aug20.A7',
        text: 'Operational data is fetched live, never uploaded',
        actors: ['Nirman'],
        quote:
          'Push that to a specific format or just say this will be fetched live ' +
          'through an API \u2026 what\u2019s the point if I need someone to download that ' +
          'log and upload it to my app?',
        actions: [
          {
            id: 'aug20.A7',
            shortId: 'A7',
            title: 'Operational/plant data framed as live API / telemetry connectors (#107)',
            state: 'shipped',
            shippedIn: 'v1.14',
            shippedCommit: 'f1a9e26',
          },
        ],
      },
      {
        id: 'aug20.A3',
        text: 'Workflows get build docs: tables + MCP tools',
        actors: ['Nirman'],
        quote:
          'You\u2019ll have another document \u2014 maybe call it workflows \u2026 the components ' +
          'would be the tables, which are your data model, and the MCP tools ' +
          'which the AI model will use. There\u2019s nothing more or less there.',
        actions: [
          {
            id: 'aug20.A3',
            shortId: 'A3',
            title: 'Workflow build docs \u2014 intent + data model per AI workflow (#105)',
            state: 'planned',
          },
        ],
      },
      {
        id: 'aug20.A2',
        text: 'On-page AI insights vs the assistant popup',
        actors: ['Miles'],
        quote:
          'To what extent are these actually going to be present on the pages ' +
          'versus something you can find out if you ask the AI system popup? ' +
          'Anything on the page is going to be the product of a workflow.',
        actions: [
          {
            id: 'aug20.A2',
            shortId: 'A2',
            title: 'Placement decision: on-page AI insights vs assistant-only (assistant.J0 / #103)',
            state: 'decided',
          },
        ],
      },
    ],
  },
  {
    id: 'catchup',
    date: '2026-08-14',
    title: 'Dev-planning catchup',
    attendees: ['Tim', 'Nirman', 'Miles'],
    inputPath: 'docs/meetings/inputs/2026-08-14-dev-planning-catchup.md',
    actionsPath: 'docs/meetings/2026-08-14-dev-planning-catchup-actions.md',
    entries: [
      {
        id: 'catchup.A1',
        text: 'Marry Paul\u2019s document with Greg\u2019s deck',
        actors: ['Tim'],
        quote:
          'I\u2019d bring Paul\u2019s document, I\u2019d bring that dashboard document and I ' +
          'try to look for how they marry \u2026 you\u2019ll very quickly find the core thinking.',
        actions: [
          {
            id: 'catchup.A1',
            shortId: 'A1',
            title: 'Marry Paul\u2019s document with Greg\u2019s deck \u2192 core data model',
            state: 'shipped',
            shippedIn: 'v1.10',
            shippedCommit: 'b2f5e85',
          },
        ],
      },
      {
        id: 'catchup.A2',
        text: 'Keep Paul\u2019s module numbering visible',
        actors: ['Nirman', 'Tim'],
        quote:
          'You can have your own grouping, maybe in a markdown file, not surface ' +
          'that into UI \u2026 when we do a version two, we can create a new baseline ' +
          'for them to refer to.',
        actions: [
          {
            id: 'catchup.A2',
            shortId: 'A2',
            title: 'Keep Paul\u2019s module numbering visible; park own grouping',
            state: 'shipped',
            shippedIn: 'v1.10',
            shippedCommit: '225b983',
          },
        ],
      },
      {
        id: 'catchup.A4',
        text: 'Ingestion as a constrained connector journey',
        actors: ['Nirman', 'Tim'],
        quote:
          'You can go to this as a connector model \u2026 still tie it back to this ' +
          'IO list, functional description.',
        actions: [
          {
            id: 'catchup.A4',
            shortId: 'A4',
            title: 'Ingestion reads as a constrained connector journey',
            state: 'shipped',
            shippedIn: 'v1.10',
            shippedCommit: '2276430',
          },
        ],
      },
      {
        id: 'catchup.A9',
        text: 'Seed-truth conflicts go to Greg, then Paul',
        actors: ['Tim'],
        quote:
          'Initial chat with Greg (second half of next week) to align on OptiFlow ' +
          'thinking; follow-up session with Paul after Greg alignment.',
        actions: [
          {
            id: 'catchup.A9',
            shortId: 'A9',
            title: 'Take D1\u2013D4 seed-truth conflicts to Greg \u2192 Paul (#28)',
            state: 'decided',
          },
        ],
      },
      {
        id: 'catchup.A6',
        text: 'The mock leads AI Foundation',
        actors: ['Nirman'],
        quote:
          'If we are building any new surfaces to AI foundation, our mock should have it first.',
        actions: [
          {
            id: 'catchup.A6',
            shortId: 'A6',
            title: 'Mock leads AI Foundation (standing principle)',
            state: 'decided',
          },
        ],
      },
    ],
  },
  {
    id: 'progressing-optiflow',
    date: '2026-08-13',
    title: 'Progressing OptiFlow',
    attendees: ['Paul', 'Tim', 'Nirman'],
    inputPath: 'docs/meetings/inputs/2026-08-13-progressing-optiflow.md',
    actionsPath: 'docs/meetings/2026-08-13-progressing-optiflow-actions.md',
    entries: [
      {
        id: 'progressing-optiflow.A1',
        text: 'Live interactive mock on Bunker Hill',
        actors: ['Paul'],
        quote: 'Build a live interactive mock of OptiFlow',
        actions: [
          {
            id: 'progressing-optiflow.A1',
            shortId: 'A1',
            title: 'Build a live interactive OptiFlow mock on Bunker Hill seed',
            state: 'shipped',
            shippedIn: 'v1.9.1 · Mobbin visual pass (2026-08-13)',
            shippedCommit: '53499c0',
          },
        ],
      },
      {
        id: 'progressing-optiflow.A2',
        text: 'Phase the mock by value',
        actors: ['Paul'],
        quote: 'Better approach: phase by value, not by sequence',
        actions: [
          {
            id: 'progressing-optiflow.A2',
            shortId: 'A2',
            title: 'Phase the mock by value, not module sequence',
            state: 'decided',
          },
        ],
      },
      {
        id: 'progressing-optiflow.A3',
        text: 'Phase 1 is ingestion plus advisory',
        actors: ['Paul'],
        quote:
          'Proposed Phase 1: modules 1 and 3 under the OptiFlow banner — generalized document ingestion plus advisory/FMEA loop',
        actions: [
          {
            id: 'progressing-optiflow.A3',
            shortId: 'A3',
            title: 'Phase 1 scope: modules 1 (ingestion) + 3 (advisory/FMEA)',
            state: 'decided',
          },
          {
            id: 'progressing-optiflow.A7',
            shortId: 'A7',
            title: 'Module 1 — process ingestion → plant digital model',
            state: 'shipped',
            shippedIn: 'v1.9.1 · Mobbin visual pass (2026-08-13)',
            shippedCommit: '53499c0',
          },
          {
            id: 'progressing-optiflow.A8',
            shortId: 'A8',
            title: 'Module 3 — advisory system + living FMEA loop',
            state: 'shipped',
            shippedIn: 'v1.9.1 · Mobbin visual pass (2026-08-13)',
            shippedCommit: '53499c0',
          },
        ],
      },
      {
        id: 'progressing-optiflow.A4',
        text: 'Merge advisory with role reporting?',
        actors: ['Paul'],
        quote: 'Paul noted modules 3 and 4 could be merged',
        actions: [
          {
            id: 'progressing-optiflow.A4',
            shortId: 'A4',
            title: 'Merge advisory (mod 3) with reporting (mod 4)?',
            state: 'shipped',
            shippedIn: 'v1.9 · Phase 1 data-driven (2026-08-13)',
            shippedCommit: 'a218364',
          },
        ],
      },
      {
        id: 'progressing-optiflow.A9',
        text: 'Role dashboards for seven plant roles',
        actors: ['Paul'],
        quote: 'Role-based views: GM sees high-level, superintendent sees detail',
        actions: [
          {
            id: 'progressing-optiflow.A9',
            shortId: 'A9',
            title: 'Role-based dashboards (7 Bunker Hill roles)',
            state: 'shipped',
            shippedIn: 'v1.9.1 · Mobbin visual pass (2026-08-13)',
            shippedCommit: '53499c0',
          },
          {
            id: 'progressing-optiflow.A11',
            shortId: 'A11',
            title: 'Replace demo personas with plant roles',
            state: 'shipped',
            shippedIn: 'v1.9.1 · Mobbin visual pass (2026-08-13)',
            shippedCommit: '53499c0',
          },
        ],
      },
      {
        id: 'progressing-optiflow.A5',
        text: 'Pinnacle versus OptiFlow entry sequence',
        actors: ['Paul'],
        quote: 'Dependency needs to be resolved early, not retrofitted later',
        actions: [
          {
            id: 'progressing-optiflow.A5',
            shortId: 'A5',
            title: 'Pinnacle ↔ OptiFlow entry sequence',
            state: 'blocked',
          },
        ],
      },
      {
        id: 'progressing-optiflow.A6',
        text: 'Live mock is Bunker Hill only',
        actors: ['Paul'],
        quote:
          'How do we capture and generalize it for multiple smelters or processing plants?',
        actions: [
          {
            id: 'progressing-optiflow.A6',
            shortId: 'A6',
            title: 'Multi-site / cross-plant generalisation (after the Bunker Hill mock)',
            state: 'decided',
          },
        ],
      },
      {
        id: 'progressing-optiflow.A12',
        text: 'Register Phase 1 product modules only',
        actors: ['Paul'],
        quote: 'Paul walked through a 13-module framework for OptiFlow',
        actions: [
          {
            id: 'progressing-optiflow.A12',
            shortId: 'A12',
            title: 'Register OptiFlow product modules in anchors + rail',
            state: 'shipped',
            shippedIn: 'v1.7 · Phase 1 modules (2026-08-13)',
          },
        ],
      },
      {
        id: 'progressing-optiflow.A13',
        text: 'Clear foundation changelog seeds',
        actors: ['Paul'],
        quote: 'Build a live interactive mock of OptiFlow',
        actions: [
          {
            id: 'progressing-optiflow.A13',
            shortId: 'A13',
            title: 'Clear foundation changelog / meeting-log seeds',
            state: 'shipped',
            shippedIn: 'v1.6 · OptiFlow fork (2026-08-13)',
          },
        ],
      },
    ],
  },
  {
    id: "paul-emails-0910",
    date: "2026-09-10",
    title: "Paul emails — recovery calculation and the AI layer by role",
    attendees: [
      "Paul",
      "Miles"
    ],
    inputPath: "docs/meetings/inputs/2026-09-10-paul-emails-role-lenses.md",
    actionsPath: 'docs/meetings/2026-09-10-paul-emails-role-lenses-actions.md',
    entries: [
      {
        id: "paul-emails-0910.A1",
        text: "Feed grade is back-calculated from concentrate and tails assays",
        actors: [
          "Paul"
        ],
        quote: "The single biggest source of error isn't the arithmetic — it's sampling … most sites lean on concentrate and tailings assays … and back-calculate feed grade",
        actions: [
          {
            id: "paul-emails-0910.A1",
            shortId: "A1",
            title: "Recovery derivation: two-product and grade-only forms; feed back-calculated from concentrate and tails assays",
            state: "decided"
          }
        ]
      },
      {
        id: "paul-emails-0910.A13",
        text: "A short brief beats another chart",
        actors: [
          "Paul"
        ],
        quote: "Probably the highest-value single element: a concise 'what changed and what to watch' summary at shift start or on-demand, rather than another dashboard the operator has to interpret cold",
        actions: [
          {
            id: "paul-emails-0910.A13",
            shortId: "A13",
            title: "Shift-start brief on the control-room landing, with superintendent and GM variants",
            state: "planned"
          }
        ]
      },
      {
        id: "paul-emails-0910.A24",
        text: "The GM lens is mine-to-mill, outside the concentrator scope",
        actors: [
          "Paul"
        ],
        quote: "this role sits outside the concentrator-only scope your module doc has been built around so far",
        actions: [
          {
            id: "paul-emails-0910.A24",
            shortId: "A24",
            title: "GM mine-to-mill lens: outside the concentrator scope, future module",
            state: "blocked"
          }
        ]
      }
    ]
  },
  {
    id: "paul-claude-tarp-0910",
    date: "2026-09-10",
    title: "Paul email — Claude session on FMEA, Control Plan and TARP",
    attendees: [
      "Paul",
      "Miles"
    ],
    inputPath: "docs/meetings/inputs/2026-09-10-paul-claude-tarp-transcript.md",
    actionsPath: 'docs/meetings/2026-09-10-paul-claude-tarp-transcript-actions.md',
    entries: [
      {
        id: "paul-claude-tarp-0910.A1",
        text: "Three documents merge into nodes, edges and actions — which the engines already are",
        actors: [
          "Paul"
        ],
        quote: "Control Plan rows become your nodes, FMEA rows become your edges, and TARPs become the action payload sitting on each edge",
        actions: [
          {
            id: "paul-claude-tarp-0910.A1",
            shortId: "A1",
            title: "Crosswalk: Control Plan → tag map and limits, FMEA → rule claims, TARP → recommended action and escalation",
            state: "decided"
          }
        ]
      },
      {
        id: "paul-claude-tarp-0910.A6",
        text: "The operator sees the current instruction and the clock, never the TARP",
        actors: [
          "Paul"
        ],
        quote: "the operator doesn't need the document's internal structure, they need the current instruction and the clock",
        actions: [
          {
            id: "paul-claude-tarp-0910.A6",
            shortId: "A6",
            title: "Timed escalation ladder on a case: tier, role, time limit, escalate-to",
            state: "planned"
          }
        ]
      }
    ]
  },
];

export function findEntry(
  meetingId: string,
  entryId: string,
): { meeting: Meeting; entry: MeetingEntry } | null {
  const meeting = MEETINGS.find((m) => m.id === meetingId);
  if (!meeting) return null;
  const entry = meeting.entries.find((e) => e.id === entryId);
  if (!entry) return null;
  return { meeting, entry };
}

const STATE_LABEL: Record<ActionState, string> = {
  shipped: 'shipped',
  'in-flight': 'in-flight',
  decided: 'decided',
  planned: 'planned',
  blocked: 'blocked',
};

export function actionStateLabel(state: ActionState): string {
  return STATE_LABEL[state];
}
